import React, { useState, useRef, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
} from "@mui/material";
import {
  Upload as UploadIcon,
  FileUpload as FileUploadIcon,
} from "@mui/icons-material";
import { useI18n } from "i18n";

interface CSVImportDialogProps {
  open: boolean;
  onClose: () => void;
  onImport: (csvData: any[]) => Promise<any>;
  onGetGradeSections?: () => Promise<{ grades: string[]; sections: string[] }>;
}

// Expected CSV format for students
const REQUIRED_HEADERS = [
  "firstName",
  "lastName",
  "gender",
  "grade",
  "section",
  "uuid",
  "major",
  "minor",
];

interface ValidationError {
  row: number;
  field: string;
  message: string;
}

interface ImportResult {
  successCount: number;
  failedCount: number;
  errors: Array<{
    row: number;
    data: any;
    error: string;
  }>;
}

export const CSVImportDialog: React.FC<CSVImportDialogProps> = ({
  open,
  onClose,
  onImport,
  onGetGradeSections,
}) => {
  const { t } = useI18n();
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [csvData, setCsvData] = useState<any[]>([]);
  const [csvHeaders, setCsvHeaders] = useState<string[]>([]);
  const [error, setError] = useState<string>("");
  const [validationErrors, setValidationErrors] = useState<ValidationError[]>(
    []
  );
  const [isImporting, setIsImporting] = useState(false);
  const [availableGrades, setAvailableGrades] = useState<string[]>([]);
  const [availableSections, setAvailableSections] = useState<string[]>([]);
  const [isLoadingGradeSections, setIsLoadingGradeSections] = useState(false);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Fetch grade and section data when dialog opens
  useEffect(() => {
    if (open && onGetGradeSections) {
      setIsLoadingGradeSections(true);
      onGetGradeSections()
        .then((data) => {
          setAvailableGrades(data.grades);
          setAvailableSections(data.sections);
        })
        .catch((err) => {
          console.error("Failed to fetch grade/section data:", err);
        })
        .finally(() => {
          setIsLoadingGradeSections(false);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.name.toLowerCase().endsWith(".csv")) {
      setError(t("csv.selectCsvError"));
      return;
    }

    setCsvFile(file);
    setError("");

    // Read and parse CSV file
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result as string;
      parseCSV(text);
    };
    reader.readAsText(file);
  };

  const parseCSV = (text: string) => {
    try {
      const lines = text.split("\n").filter((line) => line.trim());
      if (lines.length === 0) {
        setError(t("csv.emptyFileError"));
        return;
      }

      // Parse headers
      const headers = lines[0]
        .split(",")
        .map((header) => header.trim().replace(/"/g, ""));
      setCsvHeaders(headers);

      // Validate headers format
      const headerValidation = validateHeaders(headers);
      if (!headerValidation.isValid) {
        setError(headerValidation.message);
        return;
      }

      // Parse data rows
      const data = lines.slice(1).map((line, index) => {
        const values = line
          .split(",")
          .map((value) => value.trim().replace(/"/g, ""));
        const row: any = { _rowIndex: index + 2 }; // +2 because we start from line 2 in CSV
        headers.forEach((header, headerIndex) => {
          row[header] = values[headerIndex] || "";
        });
        return row;
      });

      // Validate data format
      const dataValidation = validateCSVData(data);
      setValidationErrors(dataValidation);

      setCsvData(data);
    } catch (err) {
      setError(t("csv.parseError"));
      console.error(err);
    }
  };

  const validateHeaders = (headers: string[]) => {
    const missingHeaders = REQUIRED_HEADERS.filter(
      (required) =>
        !headers.some(
          (header) => header.toLowerCase() === required.toLowerCase()
        )
    );

    if (missingHeaders.length > 0) {
      return {
        isValid: false,
        message: t("csv.missingRequiredColumns", {
          missingColumns: missingHeaders.join(", "),
          requiredColumns: REQUIRED_HEADERS.join(", "),
        }),
      };
    }

    return { isValid: true, message: "" };
  };

  const validateCSVData = (data: any[]): ValidationError[] => {
    const errors: ValidationError[] = [];

    data.forEach((row, index) => {
      // Check required fields
      REQUIRED_HEADERS.forEach((header) => {
        const value = row[header];
        if (!value || value.toString().trim() === "") {
          errors.push({
            row: row._rowIndex,
            field: header,
            message: t("csv.requiredField", { field: header }),
          });
        }
      });

      // Validate gender values if provided
      if (row.gender && row.gender.trim() !== "") {
        const validGenders = ["MALE", "FEMALE"];
        if (!validGenders.includes(row.gender.trim())) {
          errors.push({
            row: row._rowIndex,
            field: "gender",
            message: t("csv.genderError"),
          });
        }
      }

      // Validate grade values if available grades are loaded
      if (row.grade && row.grade.trim() !== "" && availableGrades.length > 0) {
        if (!availableGrades.includes(row.grade.trim())) {
          errors.push({
            row: row._rowIndex,
            field: "grade",
            message: t("csv.gradeError", { values: availableGrades.join(", ") }),
          });
        }
      }

      // Validate section values if available sections are loaded
      if (
        row.section &&
        row.section.trim() !== "" &&
        availableSections.length > 0
      ) {
        if (!availableSections.includes(row.section.trim())) {
          errors.push({
            row: row._rowIndex,
            field: "section",
            message: t("csv.sectionError", {
              values: availableSections.join(", "),
            }),
          });
        }
      }

      // Validate UUID format if provided
      if (row.uuid && row.uuid.trim() !== "") {
        const uuidRegex =
          /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
        if (!uuidRegex.test(row.uuid.trim())) {
          errors.push({
            row: row._rowIndex,
            field: "uuid",
            message: t("csv.uuidError"),
          });
        }
      }

      // Validate Major value if provided
      if (row.major && row.major.trim() !== "") {
        const majorValue = parseInt(row.major.trim(), 10);
        if (isNaN(majorValue) || majorValue < 0 || majorValue > 65535) {
          errors.push({
            row: row._rowIndex,
            field: "major",
            message: t("csv.majorError"),
          });
        }
      }

      // Validate Minor value if provided
      if (row.minor && row.minor.trim() !== "") {
        const minorValue = parseInt(row.minor.trim(), 10);
        if (isNaN(minorValue) || minorValue < 0 || minorValue > 65535) {
          errors.push({
            row: row._rowIndex,
            field: "minor",
            message: t("csv.minorError"),
          });
        }
      }

      // Validate beacon information consistency
      const hasUuid = row.uuid && row.uuid.trim() !== "";
      const hasMajor = row.major && row.major.trim() !== "";
      const hasMinor = row.minor && row.minor.trim() !== "";

      if (
        (hasUuid || hasMajor || hasMinor) &&
        !(hasUuid && hasMajor && hasMinor)
      ) {
        errors.push({
          row: row._rowIndex,
          field: "beacon",
          message: t("csv.beaconRequiredError"),
        });
      }
    });

    return errors;
  };

  const handleImport = async () => {
    if (!csvData.length) {
      setError(t("csv.noDataError"));
      return;
    }

    if (validationErrors.length > 0) {
      setError(t("csv.fixValidationError"));
      return;
    }

    setIsImporting(true);
    setError("");
    setImportResult(null);

    try {
      const response = await onImport(csvData);
      const { data: result } = response;
      console.log("Import result:", result);

      if (result.result === "success" || result.result === "partial_success") {
        setImportResult({
          successCount: result.data.successCount,
          failedCount: result.data.failedCount,
          errors: result.data.errors || [],
        });

        if (result.result === "success") {
          setError("");
        } else {
          setError(
            t("csv.partialSuccess", {
              successCount: result.data.successCount,
              failedCount: result.data.failedCount,
            })
          );
        }
      } else {
        throw new Error(result.message || t("csv.importFailed"));
      }
    } catch (err: any) {
      setError(err.message || t("csv.importDataFailed"));
      console.error("Import error:", err);
    } finally {
      setIsImporting(false);
    }
  };

  const handleClose = () => {
    setCsvFile(null);
    setCsvData([]);
    setCsvHeaders([]);
    setError("");
    setValidationErrors([]);
    setAvailableGrades([]);
    setAvailableSections([]);
    setImportResult(null);
    setIsImporting(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    onClose();
  };

  const handleFileButtonClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: { height: "80vh" },
      }}
    >
      <DialogTitle>{t("csv.title")}</DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 3 }}>
          <input
            type="file"
            accept=".csv"
            onChange={handleFileSelect}
            style={{ display: "none" }}
            ref={fileInputRef}
          />
          <Button
            variant="outlined"
            startIcon={<FileUploadIcon />}
            onClick={handleFileButtonClick}
            sx={{ mb: 2 }}
          >
            {t("csv.selectFile")}
          </Button>

          {csvFile && (
            <Typography variant="body2" color="text.secondary">
              {t("csv.selectedFile", { fileName: csvFile.name })}
            </Typography>
          )}

          {/* CSV Format Requirements */}
          <Box sx={{ mt: 2, p: 2, bgcolor: "grey.50", borderRadius: 1 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: "bold", mb: 1 }}>
              {t("csv.formatRequirements")}
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              <strong>{t("csv.requiredColumns")}</strong>{" "}
              {REQUIRED_HEADERS.join(", ")}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              • {t("csv.genderRequirement")}
              <br />
              • {t("csv.uuidRequirement")}
              <br />
              • {t("csv.majorMinorRequirement")}
              <br />• {t("csv.beaconDataRequirement")}
            </Typography>

            {/* Display available grades and sections */}
            {isLoadingGradeSections && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                {t("csv.loadingGradeSections")}
              </Typography>
            )}

            {!isLoadingGradeSections && availableGrades.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                  {t("csv.availableGrades")}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 1 }}
                >
                  {availableGrades.join(", ")}
                </Typography>
              </Box>
            )}

            {!isLoadingGradeSections && availableSections.length > 0 && (
              <Box>
                <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                  {t("csv.availableSections")}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {availableSections.join(", ")}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {validationErrors.length > 0 && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: "bold", mb: 1 }}>
              {t("csv.validationErrors", { count: validationErrors.length })}
            </Typography>
            <Box sx={{ maxHeight: 150, overflow: "auto" }}>
              {validationErrors.map((err, index) => (
                <Typography key={index} variant="body2" sx={{ mb: 0.5 }}>
                  • {t("csv.rowError", {
                    row: err.row,
                    field: err.field,
                    message: err.message,
                  })}
                </Typography>
              ))}
            </Box>
          </Alert>
        )}

        {importResult && (
          <Alert
            severity={importResult.failedCount > 0 ? "warning" : "success"}
            sx={{ mb: 2 }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: "bold", mb: 1 }}>
              {t("csv.importResults")}
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              • {t("csv.importedCount", { count: importResult.successCount })}
            </Typography>
            {importResult.failedCount > 0 && (
              <>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  • {t("csv.failedCount", { count: importResult.failedCount })}
                </Typography>
                {importResult.errors.length > 0 && (
                  <Box sx={{ maxHeight: 150, overflow: "auto", mt: 1 }}>
                    <Typography
                      variant="subtitle2"
                      sx={{ fontWeight: "bold", mb: 0.5 }}
                    >
                      {t("csv.errorDetails")}
                    </Typography>
                    {importResult.errors.map((err, index) => (
                      <Typography
                        key={index}
                        variant="body2"
                        sx={{ mb: 0.5, fontSize: "0.8rem" }}
                      >
                        • {t("csv.rowErrorSimple", {
                          row: err.row,
                          message: err.error,
                        })}
                      </Typography>
                    ))}
                  </Box>
                )}
              </>
            )}
          </Alert>
        )}

        {csvData.length > 0 && (
          <Box>
            <Typography variant="h6" sx={{ mb: 2 }}>
              {t("csv.preview", { count: csvData.length })}
              {validationErrors.length > 0 && (
                <Typography
                  component="span"
                  color="warning.main"
                  sx={{ ml: 1, fontSize: "0.875rem" }}
                >
                  {t("csv.validationErrorCount", {
                    count: validationErrors.length,
                  })}
                </Typography>
              )}
            </Typography>
            <TableContainer
              component={Paper}
              sx={{ maxHeight: 400, overflow: "auto" }}
            >
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    {csvHeaders.map((header, index) => (
                      <TableCell
                        key={index}
                        sx={{
                          fontWeight: "bold",
                          backgroundColor: REQUIRED_HEADERS.includes(header)
                            ? "primary.light"
                            : "grey.100",
                          color: REQUIRED_HEADERS.includes(header)
                            ? "primary.contrastText"
                            : "text.primary",
                        }}
                      >
                        {header}
                        {REQUIRED_HEADERS.includes(header) && (
                          <Typography
                            component="span"
                            sx={{ ml: 0.5, fontSize: "0.75rem" }}
                          >
                            *
                          </Typography>
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {csvData.slice(0, 50).map((row, index) => {
                    const rowErrors = validationErrors.filter(
                      (err) => err.row === row._rowIndex
                    );
                    const hasErrors = rowErrors.length > 0;

                    return (
                      <TableRow
                        key={index}
                        sx={{
                          backgroundColor: hasErrors
                            ? "error.light"
                            : "inherit",
                          "&:hover": {
                            backgroundColor: hasErrors
                              ? "error.main"
                              : "grey.50",
                          },
                        }}
                      >
                        {csvHeaders.map((header, headerIndex) => {
                          const cellErrors = rowErrors.filter(
                            (err) => err.field === header
                          );
                          const hasCellErrors = cellErrors.length > 0;

                          return (
                            <TableCell
                              key={headerIndex}
                              sx={{
                                border: hasCellErrors ? 2 : 0,
                                borderColor: hasCellErrors
                                  ? "error.main"
                                  : "transparent",
                                borderStyle: "solid",
                              }}
                            >
                              {row[header] || ""}
                              {hasCellErrors && (
                                <Typography
                                  variant="caption"
                                  color="error"
                                  sx={{ display: "block", fontSize: "0.6rem" }}
                                >
                                  {cellErrors[0].message}
                                </Typography>
                              )}
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    );
                  })}
                  {csvData.length > 50 && (
                    <TableRow>
                      <TableCell colSpan={csvHeaders.length} align="center">
                        <Typography variant="body2" color="text.secondary">
                          {t("csv.moreRows", { count: csvData.length - 50 })}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isImporting}>
          {t("common.close")}
        </Button>

        <Button
          onClick={handleImport}
          variant="contained"
          startIcon={<UploadIcon />}
          disabled={
            !csvData.length ||
            isImporting ||
            validationErrors.length > 0 ||
            (importResult ? importResult.successCount > 0 : false)
          }
        >
          {isImporting
            ? t("csv.importing")
            : importResult && importResult.successCount > 0
            ? t("csv.importComplete")
            : t("csv.importData")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
